"use client";

import { useQuery } from "@tanstack/react-query";
import gql from "graphql-tag";
import type { GeneratedQuery } from "@aws-amplify/api-graphql";
import type {
  PatientJourneyFacts,
  PatientJourneySummary,
  PatientJourneyVerdict,
  PatientJourneyVerdictInput,
} from "@rmh/defs";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";
import { patientJourneyVerdictQueryKey } from "./keys";

interface PatientJourneyVerdictQueryVariables {
  input: PatientJourneyVerdictInput;
}

interface PatientJourneyVerdictQueryData {
  patientJourneyVerdict?: PatientJourneyVerdict | null;
}

export const patientJourneyVerdictQuery = gql`
  query patientJourneyVerdict($input: PatientJourneyVerdictInput!) {
    patientJourneyVerdict(input: $input) {
      headline
      narrative
      category
      confidence
      findings
      unknowns
      nextSteps
    }
  }
`.loc?.source.body as GeneratedQuery<
  PatientJourneyVerdictQueryVariables,
  PatientJourneyVerdictQueryData
>;

const toVerdictInput = (
  facts: PatientJourneyFacts,
  summaries: PatientJourneySummary[]
): PatientJourneyVerdictInput => ({
  identity: {
    name: facts.identity.name,
    email: facts.identity.email,
    dateOfBirth: facts.identity.dateOfBirth,
    notes: facts.identity.notes,
  },
  lookupKey: {
    email: facts.lookupKey.email,
    source: facts.lookupKey.source,
    note: facts.lookupKey.note,
  },
  facts: facts.facts.map((fact) => ({
    key: fact.key,
    label: fact.label,
    value: fact.value,
    source: fact.source,
  })),
  sources: facts.sources.map((report) => ({
    source: report.source,
    status: report.status,
    ms: report.ms,
    note: report.note,
    incomplete: report.incomplete
      ? {
          unmappable: report.incomplete.unmappable,
          truncated: report.incomplete.truncated,
          dropped: report.incomplete.dropped,
        }
      : null,
  })),
  timeline: facts.timeline.map((event) => ({
    key: event.key,
    at: event.at,
    source: event.source,
    title: event.title,
    detail: event.detail,
    href: event.href,
    zone: event.zone,
    summaryKind: event.summaryKind,
    summaryRef: event.summaryRef,
  })),
  // What was read inside the records the report explained for itself. Without
  // them the diagnosis reasons about a replay from its duration and about a
  // check from its error type.
  summaries: summaries.map((summary) => ({
    kind: summary.kind,
    ref: summary.ref,
    headline: summary.headline,
    narrative: summary.narrative,
    findings: summary.findings,
    unknowns: summary.unknowns,
  })),
});

interface UsePatientJourneyVerdictOptions {
  /** The evidence to diagnose, or undefined while the first request is still out. */
  facts?: PatientJourneyFacts;
  /** The records the report already read back and explained. */
  summaries?: PatientJourneySummary[];
  /** False while those reads are still out: the diagnosis is grounded in them. */
  enabled?: boolean;
}

export const usePatientJourneyVerdict = ({
  facts,
  summaries = [],
  enabled = true,
}: UsePatientJourneyVerdictOptions) => {
  return useQuery<PatientJourneyVerdict>({
    queryKey: patientJourneyVerdictQueryKey(facts?.input ?? ""),
    queryFn: async () => {
      if (!facts) {
        throw new Error("There is no evidence to diagnose.");
      }

      const response = await fetchGraphQLClient(patientJourneyVerdictQuery, {
        input: toVerdictInput(facts, summaries),
      });
      const verdict = response.data?.patientJourneyVerdict;

      if (!verdict) {
        throw new Error("The Patient Journey diagnosis returned no verdict.");
      }

      return verdict;
    },
    enabled: enabled && Boolean(facts),
    staleTime: Infinity,
    // Held for the whole browser session so an earlier patient restores
    // from the cache instead of re-running the sources.
    gcTime: Infinity,
    retry: false,
  });
};
